/**
 * Les inscriptions des enfants affichés dans la liste d'attente, sur les
 * dates des sessions affichées : de quoi dire, ligne par ligne, où l'enfant
 * est déjà inscrit cette semaine-là (`semaine.ts`).
 *
 * La lecture se borne aux enfants et aux dates à l'écran, par paquets
 * d'enfants (l'adresse d'une requête a une longueur maximale) et par pages
 * (PostgREST ne rend jamais plus de 1 000 lignes, sans le dire). Comme
 * `create_order`, elle ne retient que les inscriptions payées ou en attente
 * de paiement ; les annulées sont écartées ensuite par leur statut.
 */
import { db } from '../payments/db';
import type { InscriptionDeLEnfant } from './semaine';

const ENFANTS_PAR_REQUETE = 100;
const TAILLE_DE_PAGE = 1000;

interface LigneLue {
  id: string;
  kid_id: string;
  activity_id: string;
  cancellation_status: string | null;
  sessions: SessionLue | SessionLue[] | null;
}

interface SessionLue {
  start_date: string;
  end_date: string;
  stage: { title: string } | { title: string }[] | null;
  center: { name: string } | { name: string }[] | null;
}

const premier = <T,>(v: T | T[] | null): T | null => (Array.isArray(v) ? v[0] ?? null : v);

export async function lireInscriptionsDeLaSemaine(
  kidIds: readonly string[],
  debut: string,
  fin: string,
): Promise<InscriptionDeLEnfant[]> {
  const inscriptions: InscriptionDeLEnfant[] = [];

  for (let i = 0; i < kidIds.length; i += ENFANTS_PAR_REQUETE) {
    const paquet = kidIds.slice(i, i + ENFANTS_PAR_REQUETE);
    for (let depuis = 0; ; depuis += TAILLE_DE_PAGE) {
      const { data, error } = await db
        .from('registrations')
        .select(`
          id, kid_id, activity_id, cancellation_status,
          sessions!inner(start_date, end_date, stage:stages(title), center:centers(name))
        `)
        .in('kid_id', paquet)
        .in('payment_status', ['paid', 'pending'])
        .lte('sessions.start_date', fin)
        .gte('sessions.end_date', debut)
        .order('id')
        .range(depuis, depuis + TAILLE_DE_PAGE - 1);

      if (error) throw error;
      const lignes = (data ?? []) as unknown as LigneLue[];
      for (const l of lignes) {
        const session = premier(l.sessions);
        if (!session) continue;
        inscriptions.push({
          id: l.id,
          kid_id: l.kid_id,
          activity_id: l.activity_id,
          cancellation_status: l.cancellation_status,
          start_date: session.start_date,
          end_date: session.end_date,
          stage: premier(session.stage)?.title ?? 'Stage inconnu',
          centre: premier(session.center)?.name ?? 'Centre inconnu',
        });
      }
      if (lignes.length < TAILLE_DE_PAGE) break;
    }
  }

  return inscriptions;
}
